import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { PERMISSIONS } from "../../../../../rbac";
import { getdebridementAsync, patchDebridementAsync } from "../../../../../app/patientSlice";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { getCareSetting } from "../debridement/debridementConfig";
import {
  visiblePrerequisites,
  complianceFlags,
  readinessCount,
  missingReasons,
  requiresApproach,
  showsSubcutaneousSubList,
  stateFromResponse,
} from "../debridement/debridementRules";
import { buildDebridementPayload } from "../debridement/debridementPayload";
import { useLoading } from "../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";

export const useDebridement = ({ interventionId, formularyId }) => {
  const { control, register, handleSubmit, reset, errors, clearErrors } = useForm();
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams();

  const submitLoading = useLoading(LOADING_KEYS.UPDATE_DEBRIDEMENT);
  const viewLoading = useLoading(LOADING_KEYS.GET_DEBRIDEMENT);

  const resp = useSelector((state) => state.patient.debridement);
  const patientData = useSelector((state) => state.patient.detailViewData);
  const { hasPermission } = usePermissions();

  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [readyOpen, setReadyOpen] = useState(true);
  const [showReasonErrors, setShowReasonErrors] = useState(false);
  const [form, setForm] = useState(() => stateFromResponse(null));

  const instrumentsUsedRef = useRef(null);
  const necroticTissueRef = useRef(null);

  const careSetting = useMemo(() => getCareSetting(patientData), [patientData]);

  const hideButtons = hasPermission(PERMISSIONS.ASSESSMENT_EXTERNAL_OWN_CUSTOMER) && patientData?.isOwnCustomer === 1;

  useEffect(() => {
    if (patientId && assessmentId) {
      dispatch(getdebridementAsync({ patientId, assessmentId, interventionId }));
    }
  }, [dispatch, patientId, assessmentId, interventionId]);

  useEffect(() => {
    setForm(stateFromResponse(resp));
    resp === null ? setEditable(true) : setEditable(false);
  }, [resp]);

  /* ---- derived ---- */

  const prerequisites = useMemo(() => visiblePrerequisites(careSetting), [careSetting]);

  const flags = useMemo(() => complianceFlags(careSetting, form.prerequisiteAnswers), [careSetting, form.prerequisiteAnswers]);

  const readiness = useMemo(() => readinessCount(careSetting, form.prerequisiteAnswers), [careSetting, form.prerequisiteAnswers]);

  const reasonGaps = useMemo(() => missingReasons(careSetting, form.prerequisiteAnswers), [careSetting, form.prerequisiteAnswers]);

  const performed = form.isDebridementPerformed === "yes";
  const showApproach = performed && requiresApproach(form.surgicalDebridementProcedure);
  const showSubcutaneous = performed && showsSubcutaneousSubList(form.depthOfDebridement);
  const showPressure = performed && Boolean(form.postDebridementBleedingAmount) && form.postDebridementBleedingAmount !== "none";

  useEffect(() => {
    if (!performed) {
      clearErrors();
      setShowReasonErrors(false);
    }
  }, [performed, clearErrors]);

  const setField = useCallback((name, value) => {
    setFormChangesConfirm(true);
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "surgicalDebridementProcedure") next.nonExcisionalApproach = "";

      if (name === "depthOfDebridement" && !showsSubcutaneousSubList(value)) {
        next.necroticSubcutaneousTissue = [];
      }

      if (name === "postDebridementBleedingAmount" && (!value || value === "none")) {
        next.stoppedBleedingContactPressure = "";
      }

      return next;
    });
  }, []);

  const toggleMulti = useCallback((name, key) => {
    setFormChangesConfirm(true);
    setForm((prev) => {
      const list = Array.isArray(prev[name]) ? prev[name] : [];
      return {
        ...prev,
        [name]: list.includes(key) ? list.filter((k) => k !== key) : [...list, key],
      };
    });
  }, []);

  const handleMultiSelect = useCallback((name, options, ref) => {
    setFormChangesConfirm(true);

    const selectedKeys = Array.isArray(options) ? options.map((item) => item.key) : [];

    setForm((prev) => ({
      ...prev,
      [name]: selectedKeys,
    }));
  }, []);

  const setPrerequisiteAnswer = useCallback((key, value) => {
    setFormChangesConfirm(true);
    setForm((prev) => ({
      ...prev,
      prerequisiteAnswers: {
        ...prev.prerequisiteAnswers,
        [key]: {
          ...(prev.prerequisiteAnswers?.[key] || {}),
          value,
          ...(value === "no" ? {} : { reasonCode: null, reasonText: null }),
        },
      },
    }));
  }, []);

  const setPrerequisiteSelection = useCallback((key, selection) => {
    setFormChangesConfirm(true);
    setForm((prev) => ({
      ...prev,
      prerequisiteAnswers: {
        ...prev.prerequisiteAnswers,
        [key]: {
          ...(prev.prerequisiteAnswers?.[key] || {}),
          selection,
          value: prev.prerequisiteAnswers?.[key]?.value || (selection ? "yes" : null),
        },
      },
    }));
  }, []);

  const setPrerequisiteReason = useCallback((key, reasonCode, reasonText) => {
    setFormChangesConfirm(true);
    setForm((prev) => ({
      ...prev,
      prerequisiteAnswers: {
        ...prev.prerequisiteAnswers,
        [key]: { ...(prev.prerequisiteAnswers?.[key] || {}), value: "no", reasonCode, reasonText },
      },
    }));
  }, []);

  const doSubmit = useCallback(() => {
    if (reasonGaps.length > 0) {
      setShowReasonErrors(true);
      setReadyOpen(true);
      return;
    }

    const payload = {
      state: form,
      careSetting,
      ids: { patientId, assessmentId, interventionId },
    };
    const finalPayload = buildDebridementPayload(payload);

    dispatch(patchDebridementAsync(finalPayload, payload?.ids, setEditable));

    reset();
    setShowReasonErrors(false);
    setFormChangesConfirm(false);
  }, [dispatch, reset, form, careSetting, reasonGaps.length, patientId, assessmentId, interventionId]);

  const editMode = useCallback(() => {
    setForm(stateFromResponse(resp));
    setEditable(true);
    setReadyOpen(true);
    setShowReasonErrors(false);
    setFormChangesConfirm(false);
  }, [resp]);

  const handleCancelClick = useCallback(() => {
    if (formChangesConfirm) setConfirmationModal(true);
    else setEditable(false);
  }, [formChangesConfirm]);

  const confirmationClose = useCallback(() => setConfirmationModal(false), []);

  const yesConfirmationModalClick = useCallback(() => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setForm(stateFromResponse(resp));
  }, [resp]);

  return {
    control,
    register,
    handleSubmit,
    errors,
    doSubmit,
    submitLoading,
    viewLoading,
    resp,
    form,
    careSetting,
    prerequisites,
    flags,
    readiness,
    reasonGaps,
    showReasonErrors,
    performed,
    showApproach,
    showSubcutaneous,
    showPressure,
    readyOpen,
    setReadyOpen,
    setField,
    toggleMulti,
    setPrerequisiteAnswer,
    setPrerequisiteSelection,
    setPrerequisiteReason,
    editable,
    editMode,
    hideButtons,
    noDataMsg: !resp,
    formularyId,
    setFormChangesConfirm,
    confirmationModal,
    confirmationClose,
    yesConfirmationModalClick,
    handleCancelClick,
    instrumentsUsedRef,
    necroticTissueRef,
    handleMultiSelect,
    hasPermission,
  };
};
